import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type {
  IdentifierVerification,
  PatientDetail,
  PatientHistoryEntry,
  VisitSummary,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PhoneField,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { Section as UiSection } from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  addContact,
  addIdentifier,
  contactEnteredInError,
  endRelationship,
  identifierEnteredInError,
  patientHistory,
  patientKeys,
  patientVisits,
  retryRegistrySync,
} from '../api/patients.ts';
import { useOnline } from '../hooks/use-online.ts';
import {
  formatDateOnly,
  formatInstant,
  formatIsoDate,
  formatStoredPhone,
  formatTimeOnly,
} from './format.ts';
import { VISIT_TYPE_LABELS } from './patient-banner.tsx';
import styles from './patients.module.css';
import { DECISION_TEXT } from './consent-section.tsx';
import { ReasonDialog } from './reason-dialog.tsx';
import { IDENTIFIER_LABELS, RECEPTION_IDENTIFIER_SYSTEMS } from './registration-model.ts';

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** A record section: the ui Section, with the one action this screen puts beside a heading. */
export function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <UiSection title={title} {...(action === undefined ? {} : { actions: action })}>
      {children}
    </UiSection>
  );
}

function useInvalidatePatient(patientId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['patients', patientId] });
}

const VERIFICATION_TEXT: Record<IdentifierVerification, string> = {
  unverified: 'Not checked',
  document_seen: 'Document seen',
  registry_verified: 'Checked with the national registry',
};

/** A record marked entered in error stays visible, struck through, with its reason. */
function EnteredInErrorNote({ reason }: { reason: string | null }) {
  return (
    <span className={styles.meta}>{`Entered in error${reason === null ? '' : `: ${reason}`}`}</span>
  );
}

export function IdentifiersSection({
  patient,
  canUpdate,
}: {
  patient: PatientDetail;
  canUpdate: boolean;
}) {
  const invalidate = useInvalidatePatient(patient.id);
  const online = useOnline();
  const [adding, setAdding] = useState(false);
  const [system, setSystem] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [country, setCountry] = useState('');
  const [target, setTarget] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      addIdentifier(patient.id, {
        system: system as (typeof RECEPTION_IDENTIFIER_SYSTEMS)[number],
        value: value.trim(),
        issuingCountry: system === 'passport' ? country.trim().toUpperCase() : null,
        verification: 'document_seen',
      }),
    onSuccess: async () => {
      setAdding(false);
      setSystem(null);
      setValue('');
      setCountry('');
      await invalidate();
    },
  });
  const markError = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      identifierEnteredInError(patient.id, id, reason),
    onSuccess: async () => {
      setTarget(null);
      await invalidate();
    },
  });

  return (
    <Section
      title="Identifiers"
      {...(canUpdate && !adding
        ? {
            action: (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add ID
              </Button>
            ),
          }
        : {})}
    >
      {patient.identifiers.length === 0 ? (
        <EmptyState>No ID documents recorded.</EmptyState>
      ) : (
        <ul className={styles.recordList}>
          {patient.identifiers.map((identifier) => (
            <li
              key={identifier.id}
              className={styles.recordItem}
              data-entered-in-error={identifier.status === 'entered_in_error' || undefined}
            >
              <span>{IDENTIFIER_LABELS[identifier.system]}</span>
              <span className="wodi-id">
                {identifier.value}
                {identifier.issuingCountry === null ? '' : ` (${identifier.issuingCountry})`}
              </span>
              <span className={styles.meta}>{VERIFICATION_TEXT[identifier.verification]}</span>
              {identifier.status === 'entered_in_error' ? (
                <EnteredInErrorNote reason={identifier.statusReason} />
              ) : canUpdate && identifier.system !== 'cr_id' ? (
                <Button
                  dense
                  variant="quiet"
                  aria-label={`Mark ${IDENTIFIER_LABELS[identifier.system]} entered in error`}
                  onPress={() => {
                    markError.reset();
                    setTarget(identifier.id);
                  }}
                >
                  Entered in error
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault();
            if (system !== null && value.trim() !== '') add.mutate();
          }}
        >
          <div className={styles.identifierRow} data-passport={system === 'passport' || undefined}>
            <Select
              label="Type of ID"
              placeholder="Choose a type"
              options={RECEPTION_IDENTIFIER_SYSTEMS.map((s) => ({
                id: s,
                label: IDENTIFIER_LABELS[s],
              }))}
              value={system}
              onChange={(v) => {
                setSystem(typeof v === 'string' ? v : null);
              }}
            />
            <TextField label="Number" value={value} onChange={setValue} />
            {system === 'passport' ? (
              <TextField label="Country" value={country} onChange={setCountry} />
            ) : null}
          </div>
          {add.error === null ? null : (
            <Notice tone="error">{errorText(add.error, "The ID wasn't added. Try again.")}</Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isPending={add.isPending}
              pendingLabel="Adding…"
            >
              Add ID
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      <ReasonDialog
        title="Mark this ID entered in error?"
        isOpen={target !== null}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        confirmLabel="Mark entered in error"
        pendingLabel="Saving…"
        isPending={markError.isPending}
        error={
          markError.error === null ? null : errorText(markError.error, 'Not saved. Try again.')
        }
        onConfirm={(reason) => {
          if (target !== null) markError.mutate({ id: target, reason });
        }}
      >
        <p>It stays in the record, marked with your reason. Add the correct number separately.</p>
      </ReasonDialog>
    </Section>
  );
}

export function PhonesSection({
  patient,
  canUpdate,
}: {
  patient: PatientDetail;
  canUpdate: boolean;
}) {
  const invalidate = useInvalidatePatient(patient.id);
  const online = useOnline();
  const [adding, setAdding] = useState(false);
  const [phone, setPhone] = useState<string | null>(null);
  const [owner, setOwner] = useState<'patient' | 'someone_else'>('patient');
  const [target, setTarget] = useState<string | null>(null);
  const hasPrimary = patient.contacts.some((c) => c.isPrimary && c.status === 'active');

  const add = useMutation({
    mutationFn: (value: string) =>
      addContact(patient.id, { kind: 'mobile', value, belongsTo: owner, isPrimary: !hasPrimary }),
    onSuccess: async () => {
      setAdding(false);
      setPhone(null);
      await invalidate();
    },
  });
  const markError = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      contactEnteredInError(patient.id, id, reason),
    onSuccess: async () => {
      setTarget(null);
      await invalidate();
    },
  });

  return (
    <Section
      title="Phone numbers"
      {...(canUpdate && !adding
        ? {
            action: (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add phone
              </Button>
            ),
          }
        : {})}
    >
      {patient.contacts.length === 0 ? (
        <EmptyState>No phone numbers recorded.</EmptyState>
      ) : (
        <ul className={styles.recordList}>
          {patient.contacts.map((contact) => (
            <li
              key={contact.id}
              className={styles.recordItem}
              data-entered-in-error={contact.status === 'entered_in_error' || undefined}
            >
              <span className="wodi-num">{formatStoredPhone(contact.value)}</span>
              <span className={styles.meta}>
                {contact.belongsTo === 'patient' ? "The patient's" : "Someone else's"}
                {contact.isPrimary ? ', main number' : ''}
              </span>
              {contact.status === 'entered_in_error' ? (
                <EnteredInErrorNote reason={contact.statusReason} />
              ) : canUpdate ? (
                <Button
                  dense
                  variant="quiet"
                  aria-label={`Mark phone ${formatStoredPhone(contact.value)} entered in error`}
                  onPress={() => {
                    markError.reset();
                    setTarget(contact.id);
                  }}
                >
                  Entered in error
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault();
            if (phone !== null) add.mutate(phone);
          }}
        >
          <PhoneField
            label="Mobile number"
            value={phone}
            onChange={(v) => {
              setPhone(v);
            }}
          />
          <RadioGroup
            label="Whose phone is this?"
            orientation="horizontal"
            value={owner}
            onChange={(v) => {
              setOwner(v as typeof owner);
            }}
            options={[
              { id: 'patient', label: "The patient's" },
              { id: 'someone_else', label: "Someone else's, like a parent's" },
            ]}
          />
          {add.error === null ? null : (
            <Notice tone="error">
              {errorText(add.error, "The phone number wasn't added. Try again.")}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isPending={add.isPending}
              pendingLabel="Adding…"
            >
              Add phone
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      <ReasonDialog
        title="Mark this phone number entered in error?"
        isOpen={target !== null}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        confirmLabel="Mark entered in error"
        pendingLabel="Saving…"
        isPending={markError.isPending}
        error={
          markError.error === null ? null : errorText(markError.error, 'Not saved. Try again.')
        }
        onConfirm={(reason) => {
          if (target !== null) markError.mutate({ id: target, reason });
        }}
      />
    </Section>
  );
}

type Relationship = PatientDetail['nextOfKin'][number];

function relatedName(r: Relationship): string {
  if (r.relatedPatient !== null) return r.relatedPatient.displayName;
  if (r.relatedPerson !== null) {
    return [r.relatedPerson.givenName, r.relatedPerson.familyName].filter(Boolean).join(' ');
  }
  return 'Unknown person';
}

function RelationshipStatus({ r }: { r: Relationship }) {
  if (r.status === 'active') return null;
  return (
    <span className={styles.meta}>
      {r.status === 'ended' ? 'Ended' : 'Entered in error'}
      {r.statusReason === null ? '' : `: ${r.statusReason}`}
    </span>
  );
}

export function RelationshipsSection({
  patient,
  canUpdate,
  canCheckIn,
}: {
  patient: PatientDetail;
  canUpdate: boolean;
  canCheckIn: boolean;
}) {
  const invalidate = useInvalidatePatient(patient.id);
  const [target, setTarget] = useState<{ id: string; enteredInError: boolean } | null>(null);
  const end = useMutation({
    mutationFn: ({
      id,
      reason,
      enteredInError,
    }: {
      id: string;
      reason: string;
      enteredInError: boolean;
    }) => endRelationship(patient.id, id, { reason, enteredInError }),
    onSuccess: async () => {
      setTarget(null);
      await invalidate();
    },
  });

  const endButtons = (r: Relationship) =>
    canUpdate && r.status === 'active' ? (
      <>
        <Button
          dense
          variant="quiet"
          aria-label={`End relationship with ${relatedName(r)}`}
          onPress={() => {
            end.reset();
            setTarget({ id: r.id, enteredInError: false });
          }}
        >
          End
        </Button>
        <Button
          dense
          variant="quiet"
          aria-label={`Mark relationship with ${relatedName(r)} entered in error`}
          onPress={() => {
            end.reset();
            setTarget({ id: r.id, enteredInError: true });
          }}
        >
          Entered in error
        </Button>
      </>
    ) : null;

  return (
    <>
      <Section title="Next of kin">
        {patient.nextOfKin.length === 0 ? (
          <EmptyState>No next of kin recorded.</EmptyState>
        ) : (
          <ul className={styles.recordList}>
            {patient.nextOfKin.map((r) => (
              <li
                key={r.id}
                className={styles.recordItem}
                data-entered-in-error={r.status !== 'active' || undefined}
              >
                <span>{relatedName(r)}</span>
                <span className={styles.meta}>{r.relationship.display}</span>
                {r.relatedPerson?.phone == null ? null : (
                  <span className="wodi-num">{formatStoredPhone(r.relatedPerson.phone)}</span>
                )}
                <RelationshipStatus r={r} />
                {endButtons(r)}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Dependants">
        {patient.dependants.length === 0 ? (
          <EmptyState>No dependants registered.</EmptyState>
        ) : (
          <ul className={styles.recordList}>
            {patient.dependants.map((r) => {
              // TODO(api-contract): which side of a dependant row is the dependant. Read
              // either way: the other party is whichever patient isn't this one.
              const other =
                r.relatedPatient !== null && r.relatedPatient.id !== patient.id
                  ? r.relatedPatient
                  : null;
              const dependantId = other?.id ?? r.patientId;
              const name = other?.displayName ?? relatedName(r);
              return (
                <li
                  key={r.id}
                  className={styles.recordItem}
                  data-entered-in-error={r.status !== 'active' || undefined}
                >
                  <Link to="/patients/$patientId" params={{ patientId: dependantId }}>
                    {name}
                  </Link>
                  <span className={styles.meta}>{r.relationship.display}</span>
                  <RelationshipStatus r={r} />
                  {canCheckIn && r.status === 'active' ? (
                    <Link to="/reception" search={{ patientId: dependantId }}>
                      Check in
                    </Link>
                  ) : null}
                  {endButtons(r)}
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      {patient.principals.length === 0 ? null : (
        <Section title="Dependant of">
          <ul className={styles.recordList}>
            {patient.principals.map((r) => (
              <li
                key={r.id}
                className={styles.recordItem}
                data-entered-in-error={r.status !== 'active' || undefined}
              >
                {r.relatedPatient === null ? (
                  <span>{relatedName(r)}</span>
                ) : (
                  <Link to="/patients/$patientId" params={{ patientId: r.relatedPatient.id }}>
                    {r.relatedPatient.displayName}
                  </Link>
                )}
                <span className={styles.meta}>{r.relationship.display}</span>
                <RelationshipStatus r={r} />
                {endButtons(r)}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <ReasonDialog
        title={
          target?.enteredInError === true
            ? 'Mark this relationship entered in error?'
            : 'End this relationship?'
        }
        isOpen={target !== null}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        confirmLabel={
          target?.enteredInError === true ? 'Mark entered in error' : 'End relationship'
        }
        pendingLabel="Saving…"
        isPending={end.isPending}
        error={end.error === null ? null : errorText(end.error, 'Not saved. Try again.')}
        onConfirm={(reason) => {
          if (target !== null) end.mutate({ ...target, reason });
        }}
      />
    </>
  );
}

export function RegistrySection({
  patient,
  canUpdate,
}: {
  patient: PatientDetail;
  canUpdate: boolean;
}) {
  const invalidate = useInvalidatePatient(patient.id);
  const retry = useMutation({
    mutationFn: () => retryRegistrySync(patient.id),
    onSuccess: invalidate,
  });
  const sync = patient.registrySync;
  const canRetry = canUpdate && (sync.state === 'failed' || sync.state === 'needs_review');
  return (
    <Section title="National registry">
      <p>{sync.message}</p>
      {sync.lastSyncedAt === null ? null : (
        <p className={styles.meta}>{`Last sent ${formatInstant(sync.lastSyncedAt)}.`}</p>
      )}
      {canRetry ? (
        <div>
          <Button
            isPending={retry.isPending}
            pendingLabel="Sending…"
            onPress={() => {
              retry.mutate();
            }}
          >
            Send again
          </Button>
        </div>
      ) : null}
      {retry.error === null ? null : (
        <Notice tone="error">{errorText(retry.error, 'It was not sent. Try again.')}</Notice>
      )}
    </Section>
  );
}

const VISIT_STATUS: Record<
  VisitSummary['status'],
  { state: 'active' | 'done' | 'neutral'; text: string }
> = {
  open: { state: 'active', text: 'Open' },
  closed: { state: 'done', text: 'Closed' },
  cancelled: { state: 'neutral', text: 'Cancelled' },
};

export function VisitsSection({ patientId }: { patientId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const canBill = session !== null && can(session, 'billing.read');
  const visits = useInfiniteQuery({
    queryKey: patientKeys.visits(patientId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => patientVisits(patientId, pageParam),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = visits.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section title="Visits">
      {visits.isPending ? <LoadingBlock label="Loading visits" rows={2} /> : null}
      {visits.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void visits.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(visits.error, "Visits couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {visits.isSuccess ? (
        <Table flat aria-label="Visits">
          <TableHeader>
            <Column isRowHeader>Visit no.</Column>
            <Column>Date</Column>
            <Column>Time</Column>
            <Column>Type</Column>
            <Column>Facility</Column>
            <Column>Status</Column>
            {canBill ? <Column>Bill</Column> : null}
          </TableHeader>
          <TableBody
            items={items}
            renderEmptyState={() => 'No visits yet. Check the patient in from Reception.'}
          >
            {(visit) => (
              <Row id={visit.id}>
                <Cell>
                  <span className="wodi-id">{visit.visitNumber}</span>
                </Cell>
                <Cell>{formatDateOnly(visit.startedAt)}</Cell>
                <Cell>
                  <span className="wodi-num">{formatTimeOnly(visit.startedAt)}</span>
                </Cell>
                <Cell>{VISIT_TYPE_LABELS[visit.type]}</Cell>
                <Cell>{visit.facility.name}</Cell>
                <Cell>
                  <Tag state={VISIT_STATUS[visit.status].state}>
                    {VISIT_STATUS[visit.status].text}
                  </Tag>
                </Cell>
                {canBill ? (
                  <Cell>
                    <Link to="/cashier" search={{ visitId: visit.id }}>
                      Open bill
                    </Link>
                  </Cell>
                ) : null}
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      {visits.hasNextPage ? (
        <div>
          <Button
            isPending={visits.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void visits.fetchNextPage()}
          >
            Show earlier visits
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

function historyText(entry: PatientHistoryEntry): string {
  switch (entry.kind) {
    case 'demographics': {
      const s = entry.snapshot;
      const name = [s.name.given, s.name.middle, s.name.family].filter(Boolean).join(' ');
      const birth =
        s.birthDate === null
          ? 'no date of birth'
          : `born ${formatIsoDate(s.birthDate)}${s.birthDateEstimated ? ' (estimated)' : ''}`;
      const what = entry.version === 1 ? 'Registered' : 'Details corrected';
      const reason = entry.changeReason === null ? '' : ` Reason: ${entry.changeReason}`;
      return `${what}: ${name}, ${s.sex.display}, ${birth}.${reason}`;
    }
    case 'identifier':
      return `${IDENTIFIER_LABELS[entry.identifier.system]} ${entry.identifier.value} ${
        entry.change === 'added'
          ? 'added'
          : `entered in error: ${entry.identifier.statusReason ?? ''}`
      }`;
    case 'contact':
      return `Phone ${formatStoredPhone(entry.contact.value)} ${
        entry.change === 'added' ? 'added' : `entered in error: ${entry.contact.statusReason ?? ''}`
      }`;
    case 'relationship': {
      const r = entry.relationship;
      const verb =
        entry.change === 'added'
          ? 'added'
          : entry.change === 'ended'
            ? 'ended'
            : 'entered in error';
      const reason =
        r.statusReason === null || entry.change === 'added' ? '' : `: ${r.statusReason}`;
      return `${r.relationship.display} ${relatedName(r)} ${verb}${reason}`;
    }
    case 'consent':
      return `${entry.consent.purpose === 'patient_portal' ? 'Patient portal consent' : 'Data-sharing consent'}: ${DECISION_TEXT[entry.consent.decision].toLowerCase()}${entry.consent.reason === null ? '' : `: ${entry.consent.reason}`}`;
  }
}

export function HistorySection({ patientId }: { patientId: string }) {
  const history = useInfiniteQuery({
    queryKey: patientKeys.history(patientId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => patientHistory(patientId, pageParam),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = history.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section title="History">
      {history.isPending ? <LoadingBlock label="Loading history" rows={2} /> : null}
      {history.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void history.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(history.error, "The history couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {history.isSuccess && items.length === 0 ? (
        <EmptyState>No changes recorded yet.</EmptyState>
      ) : null}
      {items.length > 0 ? (
        <ol className={styles.history}>
          {items.map((entry, index) => (
            <li key={`${entry.at}-${String(index)}`} className={styles.historyItem}>
              <span className={styles.meta}>
                {`${formatInstant(entry.at)}, ${entry.by.displayName}`}
              </span>
              <span>{historyText(entry)}</span>
            </li>
          ))}
        </ol>
      ) : null}
      {history.hasNextPage ? (
        <div>
          <Button
            isPending={history.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void history.fetchNextPage()}
          >
            Show earlier changes
          </Button>
        </div>
      ) : null}
    </Section>
  );
}
